// Messages sortants (Slack / e-mail / SMS) : mots, états et phrases d'état — docs/DECISIONS.md §24, maquette B-06.
import type { CanalMessage, MessageDemande, StatutMessage } from '../../types';

export const LIBELLE_ETAT: Record<StatutMessage, string> = {
  a_envoyer: 'À envoyer',
  envoi_en_cours: 'Envoi en cours',
  envoye: 'Envoyé',
  erreur: 'Pas parti',
  plus_utile: 'Plus utile',
};

/** Classes du design-kit : l'état se lit au mot ET à la couleur. */
export const CLASSE_BADGE_ETAT: Record<StatutMessage, string> = {
  a_envoyer: 'badge msg-badge--a_envoyer',
  envoi_en_cours: 'badge msg-badge--envoi_en_cours',
  envoye: 'badge badge-ok',
  erreur: 'badge badge-err',
  plus_utile: 'badge badge-plain',
};

export const LIBELLE_CANAL: Record<CanalMessage, string> = { slack: 'Slack', email: 'E-mail', sms: 'SMS' };

/** « lundi 19 octobre à 10 h 02 » (avec le jour de la semaine) ou « 19 octobre à 10 h 02 ». */
export function quand(iso: string | null | undefined, avecJourSemaine = true): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const jour = d.toLocaleDateString('fr-CH', avecJourSemaine ? { weekday: 'long', day: 'numeric', month: 'long' } : { day: 'numeric', month: 'long' });
  const min = d.getMinutes();
  return `${jour} à ${d.getHours()} h${min ? ` ${String(min).padStart(2, '0')}` : ''}`;
}

/** Il reste à envoyer ce message à la main. */
export const attendUneAction = (m: { statut: StatutMessage }): boolean => m.statut === 'a_envoyer' || m.statut === 'erreur';

/** « de Camille Rochat », « d'Alexia Sestito » : l'élision devant une voyelle ou un h. */
export function dePersonne(nom: string): string {
  const n = nom.trim();
  return /^[aeiouyàâäéèêëîïôöùûüh]/i.test(n) ? `d'${n}` : `de ${n}`;
}

/** Ce qu'il faut savoir d'un message pour dire où il en est : accepté par la fiche, la liste et l'historique d'Administration. */
export interface SourceLigneEtat {
  statut: StatutMessage;
  canal: CanalMessage;
  created_at: string;
  envoye_at: string | null;
  erreur: string | null;
  envoye_par_nom?: string | null;
  envoye_par_vous?: boolean;
  mode_envoi?: 'auto' | 'manuel' | 'simule' | null;
  lance_par_nom?: string | null;
  lance_par_vous?: boolean;
  plus_utile_motif?: string | null;
}

/**
 * « Préparé le … · à publier par … », « Publié par … le … » : une ligne qui dit ce qu'il s'est passé et ce qu'il reste à faire.
 * La personne concernée lit « vous » (jamais son propre nom à la 3e personne). Le détail technique d'un échec (souvent en anglais)
 * n'est donné qu'à l'administration.
 */
export function ligneEtat(m: SourceLigneEtat, avecDetailTechnique = false): string {
  const verbe = m.canal === 'slack' ? 'Publié' : 'Envoyé';
  const verbeMin = verbe.toLowerCase();
  const quiPublie = m.canal === 'slack'
    ? (m.lance_par_vous ? `à ${verbeMin === 'publié' ? 'publier' : 'envoyer'} par vous` : m.lance_par_nom ? `à publier par ${m.lance_par_nom}` : "à publier par l'équipe gestion")
    : "à envoyer par l'équipe gestion";
  switch (m.statut) {
    case 'a_envoyer':
      return `Préparé le ${quand(m.created_at, false)} · ${quiPublie}`;
    case 'envoi_en_cours':
      return "L'envoi automatique est en cours.";
    case 'envoye':
      if (m.mode_envoi === 'simule') return `Envoyé automatiquement sur #remplacements le ${quand(m.envoye_at, false)} · simulation, Slack n'est pas encore relié`;
      return m.mode_envoi === 'auto'
        ? `Envoyé automatiquement le ${quand(m.envoye_at, false)}`
        : `${verbe}${m.envoye_par_vous ? ' par vous' : m.envoye_par_nom ? ` par ${m.envoye_par_nom}` : ''}, le ${quand(m.envoye_at, false)}`;
    case 'erreur':
      return `L'envoi automatique n'est pas parti${avecDetailTechnique && m.erreur ? ` (${m.erreur})` : ''} · ${quiPublie}`;
    case 'plus_utile':
      return m.plus_utile_motif ?? "Ce message n'est plus utile.";
  }
}

/** SMS : la ligne « sans téléphone: Léa M., Noah P. » liste les personnes à appeler, ce n'est pas un SMS à envoyer. */
export const estLigneSansTelephone = (destinataire: string | null | undefined): boolean => !!destinataire && destinataire.startsWith('sans téléphone:');

export function nomsSansTelephone(destinataire: string | null | undefined): string[] {
  if (!estLigneSansTelephone(destinataire)) return [];
  return (destinataire as string).slice('sans téléphone:'.length).split(',').map((n) => n.trim()).filter(Boolean);
}

/** « 1er appel » pour le premier message Slack d'une demande, « Relance » pour le suivant. */
export function titreSlack(rang: number): string {
  return rang === 0 ? '1er appel' : rang === 1 ? 'Relance' : `Relance ${rang}`;
}
